import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, Wallet, Receipt, FileText, AlertCircle,
  Calendar, Loader2, Download,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const eur = (n) => new Intl.NumberFormat("fi-FI", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

export default function ReportsDashboard() {
  const [data, setData] = useState(null);
  const [vat, setVat] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/reports/summary"),
      api.get("/reports/vat-periods"),
    ]).then(([s, v]) => { setData(s.data); setVat(v.data || []); })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const printPage = () => window.print();

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">Talousraportit</p>
          <h1 className="text-3xl font-display gradient-text-luxe">Tilinpäätös & ALV</h1>
          <p className="text-white/60 text-sm mt-1">Yhteenveto liikevaihdosta, kuluista, voitosta ja ALV-velasta.</p>
        </div>
        <button onClick={printPage} className="btn-luxe inline-flex items-center gap-2"><Download className="w-4 h-4" /> Tulosta raportti</button>
      </header>

      {loading && <Loader2 className="w-6 h-6 animate-spin text-white/40" />}

      {data && (
        <>
          {/* P&L summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPI icon={<Wallet className="w-4 h-4" />} label="Liikevaihto / vuosi" value={eur(data.revenue_year)} c="amber" />
            <KPI icon={<Receipt className="w-4 h-4" />} label="Kulut / vuosi" value={eur(data.expenses_year)} c="red" />
            <KPI icon={<TrendingUp className="w-4 h-4" />} label="Voitto / vuosi" value={eur(data.profit_year)} c={data.profit_year >= 0 ? "emerald" : "red"} />
            <KPI icon={<TrendingUp className="w-4 h-4" />} label="Voittomarginaali" value={`${data.profit_margin}%`} c="violet" />
          </div>

          {/* Monthly chart */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between relative z-10 mb-4">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-white/40">Trendit</p>
                <h2 className="text-lg font-display text-white">Liikevaihto · 12 kk</h2>
              </div>
            </div>
            <div className="h-72 relative z-10">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthly_revenue || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" fontSize={11} />
                  <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ background: "rgba(20,10,55,0.95)", border: "1px solid rgba(245,197,66,0.3)", borderRadius: 12 }} formatter={(v) => eur(v)} />
                  <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }} />
                  <Bar name="Liikevaihto" dataKey="revenue" fill="#f5c542" radius={[6, 6, 0, 0]} />
                  <Bar name="ALV" dataKey="vat" fill="#b478ff" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* VAT periods */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between relative z-10 mb-4">
              <h2 className="text-lg font-display text-white flex items-center gap-2">
                <Receipt className="w-4 h-4 text-amber-400" /> ALV-kaudet
              </h2>
              <p className="text-xs text-white/40">Verovelka: <span className="text-amber-300 font-mono">{eur(data.vat_owed)}</span></p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 relative z-10">
              {vat.map((q) => (
                <div key={q.quarter} className="rounded-xl bg-white/[0.03] border border-white/5 p-4">
                  <p className="text-[10px] uppercase tracking-widest text-white/40">{q.quarter}</p>
                  <p className="text-base text-white mt-1">{eur(q.revenue)}</p>
                  <p className="text-xs text-amber-300">ALV {eur(q.vat_collected)}</p>
                  <p className="text-[10px] text-white/40 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Eräpäivä {q.due_date}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Open invoices */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-2 relative z-10 mb-3">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              <h2 className="text-base font-display text-white">Avoimet myyntilaskut</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 relative z-10">
              <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Avoinna yhteensä</p>
                <p className="text-2xl font-display text-amber-300">{eur(data.open_invoices_total)}</p>
              </div>
              <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4">
                <p className="text-[10px] uppercase tracking-widest text-white/40">Laskujen määrä</p>
                <p className="text-2xl font-display text-white">{data.open_invoices_count}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ icon, label, value, c }) {
  const colors = { amber: "text-amber-300", emerald: "text-emerald-300", red: "text-red-300", violet: "text-violet-300" };
  return (
    <div className="stat-block">
      <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center ${colors[c]} mb-2`}>{icon}</div>
      <p className="text-[10px] uppercase tracking-widest text-white/40">{label}</p>
      <p className={`text-xl lg:text-2xl font-display font-bold ${colors[c]}`}>{value}</p>
    </div>
  );
}
